import { h, clear, formatTime, formatDuration, formatBytes } from '../ui/dom.js';
import {
  card,
  button,
  field,
  input,
  select,
  textarea,
  emptyState,
  openModal,
  toast,
  badge,
  readinessBadge,
  metric,
} from '../ui/components.js';
import { api, ApiError } from '../api.js';
import { can } from '../store.js';
import { kindIcon, downloadAsset } from '../ui/media.js';
import { openUploadDialog, openLinkDialog } from './media-manager.js';
import type { Page, PageContext } from '../page.js';
import {
  MEDIA_KIND_LABELS,
  type EditionMinistration,
  type MediaAsset,
  type MediaKind,
  type MediaProvider,
  type PulpitView,
} from '../types.js';

export async function ministrationPage(context: PageContext): Promise<Page> {
  const { id } = context.params;
  if (!id) return { title: 'Ministração', body: emptyState('Ministração não informada.') };

  const view = await api.get<PulpitView>(`/api/ministrations/${id}/pulpit`);
  const ministration = view.current;

  const scriptArea = textarea({
    class: 'textarea textarea--script',
    value: ministration.customScript ?? view.script,
  });

  const mediaList = h('div', { class: 'list' });

  const reload = (): void => window.location.reload();

  const saveScript = async (): Promise<void> => {
    try {
      await api.patch(`/api/ministrations/${id}`, { customScript: scriptArea.value });
      toast.success('Roteiro salvo', 'Sua versão pessoal não altera o modelo mestre.');
    } catch (error) {
      toast.error('Falha ao salvar', error instanceof ApiError ? error.message : undefined);
    }
  };

  const renderMedia = (): void => {
    clear(mediaList);
    const items = ministration.media ?? [];

    if (items.length === 0) {
      mediaList.append(
        emptyState('Nenhum material anexado. Envie slides, PDFs, imagens ou links de áudio.', '◫'),
      );
      return;
    }

    for (const asset of items) {
      mediaList.append(
        h(
          'div',
          { class: 'list__item' },
          h('div', { class: 'list__index', text: kindIcon(asset.kind) }),
          h(
            'div',
            { class: 'list__body' },
            h(
              'div',
              { class: 'row' },
              h('span', { class: 'list__title', text: asset.title }),
              asset.isOfficial ? badge('Oficial', 'success') : badge(`v${asset.version}`, 'neutral'),
            ),
            h(
              'div',
              { class: 'row small muted' },
              badge(MEDIA_KIND_LABELS[asset.kind], 'info'),
              asset.provider,
              asset.sizeBytes ? formatBytes(asset.sizeBytes) : null,
              asset.mimeType ? h('span', { class: 'faint', text: asset.mimeType }) : null,
            ),
          ),
          h(
            'div',
            { class: 'table__actions' },
            asset.embedUrl
              ? button('Abrir', {
                  size: 'sm',
                  variant: 'ghost',
                  onClick: () => window.open(asset.embedUrl as string, '_blank', 'noopener'),
                })
              : button('Baixar', {
                  size: 'sm',
                  variant: 'ghost',
                  onClick: () => void downloadAsset(asset),
                }),
            can.incorporateOfficial() && !asset.isOfficial
              ? button('Incorporar ao Modelo', {
                  size: 'sm',
                  variant: 'subtle',
                  onClick: () => void incorporate(asset),
                })
              : null,
            button('Remover', {
              size: 'sm',
              variant: 'ghost',
              disabled: asset.isOfficial && !can.manageTemplates(),
              onClick: () => void removeAsset(asset),
            }),
          ),
        ),
      );
    }
  };

  const incorporate = async (asset: MediaAsset): Promise<void> => {
    try {
      await api.post(`/api/media/${asset.id}/incorporate`);
      toast.success('Incorporado ao Modelo Oficial', 'Passará a valer para as próximas edições.');
      reload();
    } catch (error) {
      toast.error('Falha ao incorporar', error instanceof ApiError ? error.message : undefined);
    }
  };

  const removeAsset = async (asset: MediaAsset): Promise<void> => {
    try {
      await api.delete(`/api/media/${asset.id}`);
      toast.success('Material removido');
      reload();
    } catch (error) {
      toast.error('Falha ao remover', error instanceof ApiError ? error.message : undefined);
    }
  };

  const submitForReview = async (): Promise<void> => {
    try {
      await api.patch(`/api/ministrations/${id}`, { readiness: 'EM_PREPARACAO' });
      toast.success('Enviado para revisão', 'A coordenação foi notificada pelo painel de prontidão.');
      reload();
    } catch (error) {
      toast.error('Falha ao enviar', error instanceof ApiError ? error.message : undefined);
    }
  };

  renderMedia();

  const actions = [
    button('Modo Púlpito', {
      onClick: () => context.navigate(`/ministracoes/${id}/pulpito`),
    }),
    button('Voltar', { variant: 'ghost', size: 'sm', onClick: () => context.navigate('/minhas-ministracoes') }),
  ];

  return {
    title: ministration.title,
    subtitle: `${ministration.editionName ?? 'Edição'} · ${formatTime(ministration.scheduledStart)} — ${formatTime(ministration.scheduledEnd)}`,
    actions,
    body: [
      h(
        'div',
        { class: 'grid grid--4' },
        metric('Duração prevista', formatDuration(ministration.durationMin), 'accent'),
        metric('Materiais', ministration.mediaCount, 'info'),
        metric('Roteiro pessoal', ministration.customScript ? 'Sim' : 'Não', ministration.customScript ? 'success' : 'default'),
      ),
      h('div', { class: 'row' }, readinessBadge(ministration.readiness)),

      card({
        title: 'Roteiro da ministração',
        hint: 'Suas anotações e versões pessoais não sobrescrevem o modelo mestre.',
        actions: [
          button('Salvar roteiro', { size: 'sm', onClick: () => void saveScript() }),
          ministration.readiness === 'PENDENTE'
            ? null
            : button('Enviar para revisão', {
                size: 'sm',
                variant: 'ghost',
                disabled: ministration.readiness === 'HOMOLOGADO',
                onClick: () => void submitForReview(),
              }),
        ],
        body: scriptArea,
      }),

      card({
        title: 'Repositório multimídia',
        hint: 'Slides, PDFs, imagens, áudio e vídeo vinculados a esta ministração.',
        actions: [
          button('Enviar arquivo', {
            size: 'sm',
            onClick: () =>
              openUploadDialog({
                scope: 'EDITION_MINISTRATION',
                ownerId: id,
                basePath: `/api/ministrations/${id}/media`,
                onDone: reload,
              }),
          }),
          button('Vincular link', {
            size: 'sm',
            variant: 'ghost',
            onClick: () =>
              openLinkDialog({
                scope: 'EDITION_MINISTRATION',
                ownerId: id,
                onDone: reload,
              }),
          }),
        ],
        body: mediaList,
      }),
    ],
  };
}

/* ---------- Upload / link ------------------------------------------------- */

// O envio de arquivos e o vínculo de links externos foram unificados no
// gerenciador de mídias (views/media-manager.ts), usado também pelo modelo
// mestre e pelas edições. A tela do ministrador apenas abre os diálogos.


